import React, { useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';

interface ErrorMessageProps {
  isVisible: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  errors?: string[];
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  isVisible,
  onClose,
  title = "Submission Failed",
  message = "Please check your information and try again.",
  errors = [],
  autoClose = false,
  autoCloseDelay = 5000
}) => {
  useEffect(() => {
    if (isVisible && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoClose, autoCloseDelay, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className="bg-red-500 text-white p-4 md:p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="text-white mr-2 md:mr-3" size={28} />
              <h3 className="text-lg md:text-xl font-bold">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6">
          <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4">
            {message}
          </p>
          
          {errors.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 md:p-4 mb-4 md:mb-6">
              <div className="flex">
                <div className="ml-2 md:ml-3">
                  <p className="text-xs md:text-sm text-red-700 font-medium mb-2">
                    Please fix the following issues:
                  </p>
                  <ul className="text-xs md:text-sm text-red-600 space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 md:px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium text-sm md:text-base min-h-[44px]"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage;